import { SEOContentFAQ } from "../../../../components/seo/SEOContentFAQ";
import { SEOContentHowTo } from "../../../../components/seo/SEOContentHowTo";
import { SEOImage } from "../../../../components/seo/SEOImage";

export const TeleprompterApresentacoesPT = () => (
    <>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Teleprompter para PowerPoint e Apresentações: Oratória Profissional
        </h2>

        <p className="lead text-xl text-slate-300 mb-8 font-medium">
            Domine seus slides e conquiste sua audiência com segurança total. <strong>Sincronize seu roteiro de forma invisível</strong> com o PowerPoint, Google Slides ou Keynote usando o PromptNinja.
        </p>

        <p className="text-slate-300 mb-8">
            Neste guia definitivo para oradores competentes, mostramos como o PromptNinja se torna seu parceiro estratégico para apresentações de alto impacto. Aprenda como ler seu roteiro de forma natural, mantendo o contato visual com o público e evitando o medo de 'dar um branco' em momentos críticos. Descubra técnicas para integrar o teleprompter a qualquer software de slides, garantindo que você apresente com fluidez, autoridade e profissionalismo, seja em um palco presencial ou em reuniões virtuais. Transforme sua oratória e destaque-se com apresentações memoráveis e sem hesitações.
        </p>

        <SEOImage
            slug="scripts-para-aulas"
            src="scripts-for-teacher-lessons.webp"
            alt="Professor usando teleprompter para dar aula online"
            caption="O PromptNinja ajuda educadores a manterem o ritmo e o contato visual durante aulas gravadas ou ao vivo."
            width={1200}
            height={675}
        />

        <div className="bg-blue-600/10 border border-blue-500/20 rounded-2xl p-6 md:p-8 text-center mb-12">
            <h3 className="text-2xl font-bold text-white mb-4">Apresente com Confiança TED-Style</h3>
            <p className="text-slate-300 mb-6">
                Transforme seus slides em suporte visual e você na estrela da apresentação.
                O <strong>PromptNinja</strong> sincroniza seu roteiro de forma invisível para o público.
            </p>
            <a href="https://promptninja.solutionkit.com.br/#app" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full transition-all hover:scale-105 shadow-lg shadow-blue-500/25">
                Iniciar Apresentação com PromptNinja
            </a>
        </div>

        <div className="bg-gradient-to-r from-red-900/30 to-green-900/30 p-8 rounded-xl border border-slate-700 mb-12">
            <h3 className="text-3xl font-bold text-white mb-6">Apresentação: Decorar vs Teleprompter PRO</h3>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-900/70 p-6 rounded-lg border border-red-500/30">
                    <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                        <span>❌</span> Método Tradicional (Decorar)
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>📚 <strong>Prep:</strong> 2 semanas decorando roteiro</li>
                        <li>🧠 <strong>Stress:</strong> Medo de esquecer estatística crítica</li>
                        <li>😰 <strong>Mid-apresentação:</strong> Branco no slide 15 = nervos</li>
                        <li>📄 <strong>Backup:</strong> Papel na mesa (olha BAIXO = perde contato)</li>
                        <li>⏱️ <strong>Resultado:</strong> Parece inseguro, "hummm" frequente</li>
                    </ul>
                </div>
                <div className="bg-slate-900/70 p-6 rounded-lg border border-green-500/30">
                    <h3 className="font-bold text-green-400 mb-4 flex items-center gap-2">
                        <span>✅</span> PromptNinja + PowerPoint
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>📚 <strong>Prep:</strong> 10 minutos colando roteiro</li>
                        <li>🧠 <strong>Stress:</strong> Zero (roteiro sempre visível SÓ pra você)</li>
                        <li>😌 <strong>Mid-apresentação:</strong> Lê fluentemente = confiante</li>
                        <li>📱 <strong>Backup:</strong> Laptop/tablet que SÓ você vê</li>
                        <li>⏱️ <strong>Resultado:</strong> "Domina assunto", zero hesitação</li>
                    </ul>
                </div>
            </div>
            <p className="text-slate-400 text-sm mt-6 italic text-center">
                💡 Teleprompter = apresentador confiante SEM meses de decoreba.
            </p>
        </div>

        <SEOImage
            slug="scripts-para-aulas"
            src="scripts-for-presentation-classes.webp"
            alt="Apresentação de slides com teleprompter integrado"
            caption="Sincronize seus slides com seu roteiro e nunca mais perca o fio da meada em suas apresentações."
            width={1200}
            height={675}
        />

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">3 Setups Apresentação Profissional</h3>
        <div className="space-y-6 mb-12">
            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-cyan-500">
                <h3 className="text-xl font-bold text-cyan-400 mb-3">Setup #1: Presencial Auditor (Sala Conferência)</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> Laptop no pódio + projetor mostrando PPT.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li><strong>Projetor:</strong> Mostra PowerPoint fullscreen (público vê slides)</li>
                    <li><strong>Laptop (você):</strong> Modo "Presenter View" PowerPoint EM um monitor</li>
                    <li><strong>Tablet ao lado:</strong> PromptNinja com roteiro completo</li>
                    <li>Olha pro público, lê discretamente do tablet (lado do laptop)</li>
                    <li>Público vê: você olhando "pra frente" (na verdade lendo tablet invisível)</li>
                    <li>Use marcador [SLIDE X] no roteiro = sabe quando avançar PPT</li>
                </ol>
                <p className="text-green-400 text-sm mt-3">✅ <strong>Ideal pra:</strong> Pitch investidores, apresentações board, keynotes.</p>
            </div>

            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-purple-400 mb-3">Setup #2: Online Zoom/Teams (Híbrido)</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> PC/Mac + webcam + apresentação online.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li><strong>Zoom:</strong> Share Screen → seleciona SÓ PowerPoint (não "Entire Screen")</li>
                    <li><strong>Monitor 1:</strong> PowerPoint em Presenter Mode (slides + notas privadas)</li>
                    <li><strong>Monitor 2:</strong> PromptNinja fullscreen EMBAIXO da webcam</li>
                    <li>Zoom mostra: PowerPoint. Você vê: PowerPoint + PromptNinja</li>
                    <li>Lê do teleprompter olhando "pra webcam" = contato visual perfeito</li>
                    <li>Público online: só vê slides + sua cara confiante</li>
                </ol>
                <p className="text-green-400 text-sm mt-3">✅ <strong>Ideal pra:</strong> Webinars, reuniões cliente, treinamentos corporativos online.</p>
            </div>

            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-red-400 mb-3">Setup #3: Google Slides + Celular Remote</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> Laptop + celular (controle dual).
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li><strong>Laptop:</strong> Google Slides presenting + PromptNinja janela lateral</li>
                    <li><strong>Celular #1:</strong> Google Slides remote (avança slides)</li>
                    <li><strong>Celular #2:</strong> PromptNinja P2P remote (controla scroll roteiro)</li>
                    <li>Você controla: slides com mão direita, teleprompter com esquerda</li>
                    <li>Sync perfeito: avança slide = pausa teleprompter = explica visual</li>
                    <li>Retoma teleprompter = continua narrativa</li>
                </ol>
                <p className="text-yellow-400 text-sm mt-3">⚠️ <strong>Pro tip:</strong> Use comando [STOP] no roteiro = pausa automática a cada slide.</p>
            </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg my-8 border-l-4 border-orange-500">
            <h3 className="text-2xl font-bold text-white mb-4">Técnica "Segundo Cérebro" (Slides Limpos + Roteiro Rico)</h3>
            <p className="text-slate-300 mb-4">
                <strong>ERRO CLÁSSICO:</strong> Slides LOTADOS de texto porque você tem medo de esquecer. Público lê slide = IGNORA você falando.
            </p>
            <p className="text-slate-300 mb-4">
                <strong>MÉTODO PRO:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-300 text-sm">
                <li><strong>Slides:</strong> SÓ imagens/gráficos impactantes + título (máx 10 palavras)</li>
                <li><strong>PromptNinja:</strong> Roteiro COMPLETO com estatísticas, argumentos, transições</li>
                <li>Você fala: dados ricos do teleprompter. Público vê: visual limpo slide</li>
                <li>Atenção 100% em VOCÊ (não lendo slide igual robô)</li>
            </ul>
            <p className="text-green-400 text-sm mt-4">
                ✅ <strong>Resultado:</strong> Apresentação TED-style. Slides = suporte visual. Você = estrela.
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">Como Sincronizar Slides com Roteiro</h3>
        <div className="bg-slate-800 p-6 rounded-lg my-6">
            <SEOContentHowTo
                title=""
                schemaTitle="Como Sincronizar Teleprompter e Slides"
                totalTime="PT5M"
                tools={["PromptNinja", "PowerPoint ou Google Slides"]}
                steps={[
                    {
                        title: "Passo 1: Estruture Roteiro por Slide",
                        text: "Copie o texto de cada slide pro roteiro. Coloque no início de cada slide [SLIDE 1], [SLIDE 2] pra saber quando avançar."
                    },
                    {
                        title: "Passo 2: Navegue pelos slides",
                        text: "Você pode navegar pelos slides do seu roteiro usando os atalhos de setas: '→' para avançar e '←' para voltar."
                    },
                    {
                        title: "Passo 3: Use Comandos STOP Para Pausas",
                        text: "Insira [STOP] depois de cada marcador de slide. Teleprompter pausa automático = você explica visual do slide antes de continuar. Ou use o comando [PAUSE X], o que permite uma pausa por X segundos."
                    },
                    {
                        title: "Passo 4: Rehearsal com Timer",
                        text: "Primeira run-through: marque tempo REAL de cada slide. Ajuste roteiro se algum slide tá muito longo/curto. Meta: +/- 15% do planejado."
                    }
                ]}
            />
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">5 Erros Fatais de Apresentação com Teleprompter</h3>
        <div className="space-y-4 mb-12">
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Erro #1: Compartilhar Tela INTEIRA no Zoom (Expõe Teleprompter)</h4>
                <p className="text-slate-300 text-sm">
                    Zoom → Share Screen → "Entire Screen". Público VÊ tudo: PowerPoint + PromptNinja aberto + suas abas Chrome. Constrangedor.
                    <span className="text-green-400 block mt-2">✅ <strong>Solução:</strong> Share APENAS janela PowerPoint. PromptNinja fica fora do compartilhamento = invisível pro público.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Erro #2: Roteiro Desincronizado com Slides (Ordem Errada)</h4>
                <p className="text-slate-300 text-sm">
                    PowerPoint slide 10. Roteiro teleprompter = texto do slide 8. Você fala dados antigos enquanto slide mostra gráfico diferente. Público confuso.
                    <span className="text-green-400 block mt-2">✅ <strong>Solução:</strong> SEMPRE teste ANTES. Avança slides manualmente enquanto lê roteiro = verifica sincronia. Ajusta [SLIDE X] marcadores.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Erro #3: Ler "Como Robô" (Sem Variação Tom)</h4>
                <p className="text-slate-300 text-sm">
                    Usa teleprompter mas lê monótono igual GPS. Público dorme em 3min. Você parece insincero mesmo sabendo conteúdo.
                    <span className="text-green-400 block mt-2">✅ <strong>Solução:</strong> Marque ÊNFASES no roteiro (CAPS, negrito, **destaque**). Pause 2s em vírgulas importantes. ENSAIE variando tom.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Erro #4: Laptop/Tablet Longe Demais (Movimento Olhos Óbvio)</h4>
                <p className="text-slate-300 text-sm">
                    Teleprompter 2 metros À ESQUERDA. Você apresenta virando cabeça 60º pra ler. Público SABE que você tá lendo. Perde credibilidade.
                    <span className="text-green-400 block mt-2">✅ <strong>Solução:</strong> Tablet/laptop MÁXIMO 30cm de onde você olha naturalmente. Presencial: lado do pódio. Online: embaixo webcam.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Erro #5: Não Ter Backup se Laptop Travar</h4>
                <p className="text-slate-300 text-sm">
                    Apresentação ao vivo. Laptop trava (Windows update?!). Teleprompter some. Você NÃO decorou = improvisa HORRÍVEL. Cliente nota.
                    <span className="text-green-400 block mt-2">✅ <strong>Solução:</strong> PromptNinja no celular também (QR code sync). Laptop trava = pega celular discreto, continua lendo. Público nem percebe.</span>
                </p>
            </div>
        </div>

        <h3 className="text-2xl font-bold text-white mt-8 mb-4">Dicas PowerPoint/Keynote Específicas</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">📊 Presenter View</h3>
                <p className="text-sm text-slate-300">PowerPoint/Keynote tem "Presenter View": você vê notas privadas, público vê só slides. Combine COM teleprompter = notas visuais + roteiro completo.</p>
            </div>
            <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">🎨 Slides Minimalistas</h3>
                <p className="text-sm text-slate-300">Com teleprompter, slides podem ter ZERO texto. Só imagens impactantes. Você fala rico do roteiro, visual limpo.</p>
            </div>
            <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">⏱️ Auto-Advance Slides</h3>
                <p className="text-sm text-slate-300">PowerPoint pode auto-avançar slides a cada Xmin. COMBINE com comando [WAIT Xs] no teleprompter = sincronia automática perfeita.</p>
            </div>
            <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">📱 Clicker Bluetooth</h3>
                <p className="text-sm text-slate-300">Clicker avança PPT slides (mão direita). Celular controla teleprompter (mão esquerda). Full control sem tocar laptop.</p>
            </div>
        </div>

        <div className="text-center bg-slate-800 p-8 rounded-lg my-10">
            <h3 className="text-3xl font-bold text-white mb-4">🎤 Apresentações Memoráveis Sem Decorar</h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Slides impactantes + roteiro invisível = apresentador confiante que "domina assunto".
            </p>
            <a
                href="https://promptninja.solutionkit.com.br/#app"
                className="inline-block bg-gradient-to-r from-orange-500 to-red-600 font-bold py-3 px-8 rounded-full transition hover:brightness-110"
                style={{ color: 'white' }}
            >
                🚀 Prepare Apresentação Agora (Grátis)
            </a>
            <p className="text-slate-400 mt-4 text-sm">Sync com PowerPoint • Controle remoto • Invisível pro público</p>
        </div>

        <SEOContentFAQ
            title="Perguntas Frequentes - Apresentações PowerPoint/Keynote"
            items={[
                {
                    question: "O público vai perceber que estou lendo de um teleprompter?",
                    answer: "NÃO se você posicionar certo. Online: teleprompter embaixo da webcam = você olha 'pra câmera'. Presencial: laptop/tablet ao lado do pódio = olha discreto. Segredo: não FIXAR olhos no texto 100% tempo. Olhe público, volte pro texto, repita naturalmente."
                },
                {
                    question: "Funciona junto com PowerPoint Presenter Mode?",
                    answer: "PERFEITAMENTE! Presenter Mode mostra: (1) slide atual, (2) próximo slide, (3) notas privadas, (4) timer. PromptNinja = roteiro COMPLETO em segunda tela. Você tem: visual do slide + notas curtas + roteiro detalhado. Triple backup."
                },
                {
                    question: "Como sincronizo slides com o roteiro do teleprompter?",
                    answer: "Use marcadores [SLIDE 1], [SLIDE 2] em NEGRITO no roteiro. Quando vê [SLIDE 5], você avança PowerPoint manualmente (clicker ou seta). Ou use comando [STOP] = teleprompter pausa, você avança slide, retoma leitura."
                },
                {
                    question: "Posso usar em reunião Zoom compartilhando tela?",
                    answer: "SIM! Critical: compartilhe APENAS janela PowerPoint (não Entire Screen). PromptNinja fica aberto fora do compartilhamento = você vê, público NÃO vê. Teste ANTES: inicie Zoom, compartilhe, veja preview = confirma que teleprompter tá invisível."
                },
                {
                    question: "Precisa internet durante apresentação ou funciona offline?",
                    answer: "Funciona 100% OFFLINE depois carregado. Apresentação auditório sem WiFi = OK. Roteiro salvo localmente. Só precisa internet se: (1) primeiro load ou (2) controle remoto celular (usa WiFi local mas pode criar hotspot)."
                },
                {
                    question: "Como evito que PowerPoint auto-dim tela durante apresentação?",
                    answer: "Windows: Settings → System → Power → Screen timeout = Never (durante apresentação). Mac: System Preferences → Energy Saver → desabilita sleep. Ou mantenha laptop plugado = geralmente previne auto-dim."
                },
                {
                    question: "Posso usar teleprompter para Q&A depois dos slides?",
                    answer: "SIM! Prepare seção separada roteiro: 'FAQ PREPARADO'. Lista 10-15 perguntas prováveis + respostas. Durante Q&A: busca pergunta similar no teleprompter (Ctrl+F), lê resposta preparada = parece improviso mas é PERFEITO."
                },
                {
                    question: "E se eu quiser improvisar parte da apresentação (não ler tudo)?",
                    answer: "Roteiro = GUIA, não camisa-de-força. Marque seções [AD-LIB] onde você improvisa. Ou use cores: verde = leia exato (estatísticas críticas), amarelo = parafraseie livremente. Teleprompter = segurança, não prisão."
                }
            ]}
        />
    </>
);
